// Clients → Channels and tenders, Lapsed and at risk, Sellers and consignors, Unattached sales, and the tier rule
// (Client Accounts plan, section 3). Lists read clients_top (one fetch shared by every tab); writes are owner-signed.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '../../ReportsApp'
import { reportsAction, useReport } from '../../api'
import { int, money } from '../../format'
import { AsyncButton, Button, Card, Field, Tile } from '../../ui'
import { useOwnerSigner } from '../owner-edit'
import { ClientSearch } from './ClientDetail'
import type { TopData } from './TopClients'
import { Avatar, CHANNEL_LABEL, Seg, SplitBar, SplitKey, TierChip, type ClientRow, clientsCsv, dateY, daysLabel, tenderLabel } from './clients-ui'

interface ChannelsData {
  range: 'all' | 'l12'
  channels: Record<'store' | 'top' | 'core' | 'buyer', { in_person: number; online: number; staff: number; total: number }>
  tenders: { tender: string; amount: number; pct: number }[]
  no_client_by_month: { month: string; pct: number; none: number; total: number }[]
  concentration: { top10: number; top25: number; top50: number }
}
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function ClientsChannels() {
  const [range, setRange] = useState<'all' | 'l12'>('all')
  const { data: d, error } = useReport<ChannelsData>('clients_channels', { range }, [range])
  const rows: [string, keyof ChannelsData['channels']][] = [['Whole store', 'store'], ['Top tier', 'top'], ['Core', 'core'], ['Buyer', 'buyer']]
  const maxPct = Math.max(10, ...(d?.no_client_by_month ?? []).map((m) => m.pct))
  return (
    <Screen title="Channels and tenders" sub="Where clients buy and how they pay. Goods value before tax and shipping, less refunds" right={<Seg label="Range" value={range} onChange={setRange} options={[['all', 'All time'], ['l12', 'Last 12 months']]} />}>
      {error && <div className="rp-error">{error}</div>}
      <div className="rp-grid rp-grid--2e">
        <Card title="Where clients buy" note="Share of sales, whole store and by tier" right={<SplitKey />}>
          <div className="cl-rows">
            {rows.map(([l, k]) => { const c = d?.channels[k]; return (
              <div key={k} className="cl-chrow"><b>{l}</b>{c ? <SplitBar split={[c.in_person, c.online, c.staff]} height={14} /> : <span />}<span className="r rp-muted">{c ? money(c.total, true) : '…'}</span>
                {c && c.total > 0 && <small className="rp-muted">{Math.round((100 * c.in_person) / c.total)}% in person · {Math.round((100 * c.online) / c.total)}% online · {Math.round((100 * c.staff) / c.total)}% arranged by staff</small>}</div>) })}
          </div>
        </Card>
        <Card title="By tender">
          <div className="cl-rows">
            {(d?.tenders ?? []).filter((t) => t.amount > 0).map((t) => (
              <div key={t.tender} className="cl-trow"><b>{tenderLabel(t.tender) || t.tender}</b><span className="rp-bar"><i style={{ width: `${Math.max(1, t.pct)}%` }} /></span><span className="r rp-muted">{t.pct}% · {money(t.amount, true)}</span></div>
            ))}
          </div>
          <p className="rp-note">Cash, Zelle, crypto and PayPal at the counter never reach Shopify; they are counted here from the app.</p>
        </Card>
        <Card title="Sales with no client, by month" note="The number to drive to zero">
          <div className="cl-mbars">
            {(d?.no_client_by_month ?? []).map((m) => (
              <span key={m.month} className="cl-mbars__col" title={`${money(m.none, true)} of ${money(m.total, true)}`}>
                <b className={m.pct > 10 ? 'rp-tone-red' : ''}>{m.pct}%</b>
                <i style={{ height: `${(100 * m.pct) / maxPct}%` }} />
                <small>{MON[Number(m.month.slice(5)) - 1]}</small>
              </span>
            ))}
          </div>
          <p className="rp-note">Unnamed Shopify customers count as no client until someone names them. It falls as the counter and the POS tile ask for the client on every sale.</p>
        </Card>
        <Card title="How concentrated sales are" note="Share of all sales held by the biggest clients">
          <div className="cl-conc">
            <div><b>{d ? `${d.concentration.top10}%` : '…'}</b><span>Top 10 clients</span></div>
            <div><b>{d ? `${d.concentration.top25}%` : '…'}</b><span>Top 25</span></div>
            <div><b>{d ? `${d.concentration.top50}%` : '…'}</b><span>Top 50</span></div>
          </div>
        </Card>
      </div>
    </Screen>
  )
}

/** Lapsed and at risk: Top and Core clients gone quiet, where win-back starts. Logging an outreach writes to the client. */
export function ClientsLapsed() {
  const { data: d, error, refresh } = useReport<TopData>('clients_top')
  const navigate = useNavigate()
  const signer = useOwnerSigner()
  const [days, setDays] = useState<'90' | '180' | '365'>('90')
  const [open, setOpen] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [chan, setChan] = useState('text')
  const [msg, setMsg] = useState('')
  const rows = useMemo(() => (d?.clients ?? []).filter((c) => (c.tier === 'top' || c.tier === 'core') && c.days != null && c.days >= Number(days)).sort((a, b) => b.life - a.life), [d, days])
  const log = (c: ClientRow) => new Promise<void>((res, rej) => signer.signed(async () => {
    setMsg('')
    try { await reportsAction('client_note', { employee_id: signer.idRef.current, customer_id: c.id, kind: 'outreach', channel: chan, body: text }); setOpen(null); setText(''); refresh(); res() } catch (e) { setMsg((e as Error).message); rej(e) }
  }))
  const cols = 'minmax(0, 1.6fr) 92px 140px 110px 90px 160px 150px'
  return (
    <Screen title="Lapsed and at risk" sub="Top and Core clients with no purchase in a while, biggest first" right={<>
      <Seg label="No purchase in" value={days} onChange={setDays} options={[['90', '90+ days'], ['180', '180+ days'], ['365', '365+ days']]} />
      <Button onClick={() => clientsCsv(rows, `starchive-lapsed-${days}.csv`)} disabled={!d}>Export CSV</Button></>}>
      {signer.askEl}
      {(error || msg) && <div className="rp-error">{error || msg}</div>}
      <div className="rp-card">
        <div className="rp-table" role="table">
          <div className="rp-table__head" style={{ gridTemplateColumns: cols }} role="row">{['Client', 'Tier', 'Advisor', 'Lifetime', 'Since', 'Last outreach', ''].map((h, i) => <span key={i} className={[3, 4].includes(i) ? 'r' : ''}>{h}</span>)}</div>
          {d && !rows.length && <div className="rp-table__empty">Nobody in Top or Core has gone {days}+ days without buying</div>}
          {rows.map((c) => (
            <div key={c.id}>
              <div className="rp-table__row rp-table__row--link" style={{ gridTemplateColumns: cols }} role="row" onClick={() => navigate('/clients/c/' + c.id)}>
                <span className="cl-who"><b>{c.name}</b><small>{c.city || c.email}</small></span>
                <span><TierChip tier={c.tier} pinned={c.pinned} /></span>
                <span className="cl-advcell"><Avatar name={c.advisor_name} />{c.advisor_name ?? <i className="rp-tone-amber">No advisor</i>}</span>
                <span className="r"><b>{money(c.life, true)}</b></span>
                <span className="r rp-tone-red">{daysLabel(c.days)}</span>
                <span>{c.last_outreach ? dateY(c.last_outreach) : <i className="rp-muted">none logged</i>}</span>
                <span onClick={(e) => e.stopPropagation()}><Button small onClick={() => { setOpen(open === c.id ? null : c.id); setText('') }}>Log outreach</Button></span>
              </div>
              {open === c.id && (
                <div className="cl-inline">
                  <Seg label="How" value={chan} onChange={setChan} options={[['text', 'Text'], ['call', 'Call'], ['email', 'Email'], ['dm', 'DM']]} />
                  <input className="rp-input" autoFocus placeholder="What was sent or said" value={text} onChange={(e) => setText(e.target.value)} />
                  <AsyncButton primary small run={() => log(c)}>Save</AsyncButton>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Screen>
  )
}

/** Sellers and consignors: who Starchive buys from, the people to call when buying. Payouts stay in Accounting. */
export function ClientsSellers() {
  const { data: d, error } = useReport<TopData>('clients_top')
  const navigate = useNavigate()
  const rows = useMemo(() => (d?.clients ?? []).filter((c) => c.seller || c.consignor).sort((a, b) => (b.sold + b.paid_out) - (a.sold + a.paid_out) || String(b.last_intake ?? '').localeCompare(String(a.last_intake ?? ''))), [d])
  const cols = 'minmax(0, 1.5fr) 120px 120px 110px 110px 110px 110px'
  return (
    <Screen title="Sellers and consignors" sub="Everyone who has sold, traded in or consigned to Starchive" right={<Button onClick={() => clientsCsv(rows, 'starchive-sellers.csv')} disabled={!d}>Export CSV</Button>}>
      {error && <div className="rp-error">{error}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Sellers" value={d ? int(rows.filter((r) => r.seller).length) : '…'} sub={d ? `${money(rows.reduce((a, r) => a + r.sold, 0), true)} paid for ${int(rows.reduce((a, r) => a + r.sold_n, 0))} pieces` : ''} />
        <Tile label="Consignors linked to a client" value={d ? int(rows.filter((r) => r.consignor).length) : '…'} sub="Linked by the cleanup; the rest are on Consignor links" />
        <Tile label="Also buy from us" value={d ? int(rows.filter((r) => r.purchases > 0).length) : '…'} sub="Sellers and consignors with a purchase" />
      </div>
      <div className="rp-card">
        <div className="rp-table" role="table">
          <div className="rp-table__head" style={{ gridTemplateColumns: cols }} role="row">{['Client', 'Sold to us', 'Pieces', 'Consigned', 'Paid out', 'Last in', 'Bought from us'].map((h, i) => <span key={h} className={i && i !== 5 ? 'r' : ''}>{h}</span>)}</div>
          {d && !rows.length && <div className="rp-table__empty">No sellers or consignors on client records yet</div>}
          {rows.map((c) => (
            <div key={c.id} className="rp-table__row rp-table__row--link" style={{ gridTemplateColumns: cols }} role="row" onClick={() => navigate('/clients/c/' + c.id)}>
              <span className="cl-who"><b>{c.name}</b><small>{[c.consignor ? `Consignor ${c.consignor_key}` : '', c.phone].filter(Boolean).join(' · ')}</small></span>
              <span className="r">{money(c.sold, true)}</span><span className="r">{int(c.sold_n)}</span><span className="r">{int(c.consigned_n)}</span>
              <span className="r">{money(c.paid_out, true)}</span><span>{dateY(c.last_intake)}</span><span className="r">{money(c.life, true)}</span>
            </div>
          ))}
        </div>
      </div>
    </Screen>
  )
}

interface UnRow { kind: 'sale' | 'order'; ref: string; at: string; label: string; amount: number; channel: string; tender: string; source: string; served_by: string | null; name_on_sale: string; lines: { title: string; brand: string; image_url: string | null }[]; stub: boolean }
/** Unattached sales: no client, or an unnamed one. "Who bought this?" names the buyer: owner ID and a reason, audited, never guessed. */
export function ClientsUnattached() {
  const { data: d, error, refresh } = useReport<{ rows: UnRow[]; total: number; stub_total: number }>('clients_unattached')
  const signer = useOwnerSigner()
  const [show, setShow] = useState<'all' | 'none' | 'stub'>('all')
  const [open, setOpen] = useState<string | null>(null)
  const [pick, setPick] = useState<ClientRow | null>(null)
  const [reason, setReason] = useState('')
  const [msg, setMsg] = useState('')
  const [done, setDone] = useState('')
  const [limit, setLimit] = useState(60)
  const rows = useMemo(() => (d?.rows ?? []).filter((r) => show === 'all' || (show === 'stub' ? r.stub : !r.stub)), [d, show])
  const save = (r: UnRow) => new Promise<void>((res, rej) => signer.signed(async () => {
    setMsg('')
    if (!pick) { setMsg('Pick the client first'); rej(new Error('no client')); return }
    try { await reportsAction('client_name_buyer', { employee_id: signer.idRef.current, kind: r.kind, ref: r.ref, customer_id: pick.id, reason }); setDone(`${r.label} is now on ${pick.name}'s account`); setOpen(null); setPick(null); setReason(''); refresh(); res() } catch (e) { setMsg((e as Error).message); rej(e) }
  }))
  const cols = '110px minmax(0, 2fr) 110px 150px 150px 150px'
  return (
    <Screen title="Unattached sales" sub="Sales with no client, largest first. Naming a buyer needs an owner ID and a reason; nothing is guessed">
      {signer.askEl}
      {(error || msg) && <div className="rp-error">{error || msg}</div>}
      {done && <div className="rp-okbar">{done}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Sales with no client" tone="red" value={d ? money(d.total, true) : '…'} sub={d ? `${int(d.rows.length)} sales and orders` : ''} />
        <Tile label="No customer at all" value={d ? money(d.total - d.stub_total, true) : '…'} sub="Mostly card sales rung in POS with no customer" />
        <Tile label="Customer with no name" value={d ? money(d.stub_total, true) : '…'} sub="An email receipt only; name them on the match sheet or here" />
      </div>
      <div className="rp-filterbar">
        <Seg label="Show" value={show} onChange={setShow} options={[['all', 'All'], ['none', 'No customer'], ['stub', 'Unnamed customer']]} />
        <span className="rp-muted cl-small">{int(rows.length)} rows</span>
      </div>
      <div className="rp-card">
        <div className="rp-table" role="table">
          <div className="rp-table__head" style={{ gridTemplateColumns: cols }} role="row">{['Date', 'Piece', 'Amount', 'Paid', 'Rung by', ''].map((h, i) => <span key={i} className={i === 2 ? 'r' : ''}>{h}</span>)}</div>
          {rows.slice(0, limit).map((r) => (
            <div key={r.kind + r.ref}>
              <div className="rp-table__row" style={{ gridTemplateColumns: cols }} role="row">
                <span>{dateY(r.at)}</span>
                <span className="cl-piece">{r.lines[0]?.image_url ? <img src={r.lines[0].image_url} alt="" loading="lazy" /> : <i aria-hidden="true" />}<span className="cl-who"><b>{r.lines[0]?.title || r.label}{r.lines.length > 1 ? ` + ${r.lines.length - 1} more` : ''}</b><small>{r.label}{r.name_on_sale ? ` · name on sale: ${r.name_on_sale}` : ''}{r.stub ? ' · unnamed customer' : ''}</small></span></span>
                <span className="r"><b>{money(r.amount, true)}</b></span>
                <span>{tenderLabel(r.tender)} · {CHANNEL_LABEL[r.channel] ?? r.channel}</span>
                <span>{r.served_by ?? (r.source === 'shopify_history' ? 'Shopify' : '—')}</span>
                <span><Button small onClick={() => { setOpen(open === r.ref ? null : r.ref); setPick(null); setReason('') }}>Who bought this?</Button></span>
              </div>
              {open === r.ref && (
                <div className="cl-inline cl-inline--col">
                  <ClientSearch onPick={setPick} />
                  {pick && <div className="cl-panel__row"><span>Buyer: <b>{pick.name}</b></span>
                    <input className="rp-input" placeholder="How you know (e.g. Shane rang it up for him)" value={reason} onChange={(e) => setReason(e.target.value)} />
                    <AsyncButton primary small run={() => save(r)}>Save</AsyncButton></div>}
                </div>
              )}
            </div>
          ))}
        </div>
        {rows.length > limit && <div className="cl-more"><Button small onClick={() => setLimit(limit + 100)}>Show more</Button></div>}
      </div>
    </Screen>
  )
}

/** The tier rule and the switch that shows Clients in the rail. */
export function ClientsSettings() {
  const { data: d, error, refresh } = useReport<TopData>('clients_top')
  const signer = useOwnerSigner()
  const cfg = d?.config
  const [f, setF] = useState<Record<string, string> | null>(null)
  const v = f ?? (cfg ? { top: String(cfg.top_cents / 100), top_purchases: String(cfg.top_purchases), core: String(cfg.core_cents / 100), months: String(cfg.months), lapsed_days: String(cfg.lapsed_days) } : null)
  const [msg, setMsg] = useState('')
  const save = (extra: Record<string, unknown> = {}) => new Promise<void>((res, rej) => signer.signed(async () => {
    setMsg('')
    try { await reportsAction('clients_settings', { employee_id: signer.idRef.current, ...(v ?? {}), ...extra }); setF(null); refresh(); res() } catch (e) { setMsg((e as Error).message); rej(e) }
  }))
  return (
    <Screen title="Client settings" sub="The tier rule. Tiers are computed, not felt; an owner can pin one on the client's page">
      {signer.askEl}
      {(error || msg) && <div className="rp-error">{error || msg}</div>}
      {v && <Card title="Tier rule">
        <div className="cl-panel__grid cl-pad">
          <Field label="Top: spend at least ($)"><input className="rp-input" inputMode="decimal" value={v.top} onChange={(e) => setF({ ...v, top: e.target.value })} /></Field>
          <Field label="…or this many purchases"><input className="rp-input" inputMode="numeric" value={v.top_purchases} onChange={(e) => setF({ ...v, top_purchases: e.target.value })} /></Field>
          <Field label="Core: spend at least ($)"><input className="rp-input" inputMode="decimal" value={v.core} onChange={(e) => setF({ ...v, core: e.target.value })} /></Field>
          <Field label="Counted over (months)" hint="0 counts all time"><input className="rp-input" inputMode="numeric" value={v.months} onChange={(e) => setF({ ...v, months: e.target.value })} /></Field>
          <Field label="Lapsed after (days)"><input className="rp-input" inputMode="numeric" value={v.lapsed_days} onChange={(e) => setF({ ...v, lapsed_days: e.target.value })} /></Field>
        </div>
        <div className="cl-pad"><p className="rp-note">Buyer is anyone with a purchase below Core; Prospect is a named client who has not bought yet. Staff, owners, house accounts and unnamed customers never get a tier.</p>
          <AsyncButton primary run={() => save()} doneLabel="Saved">Save the rule</AsyncButton></div>
      </Card>}
      {cfg && <Card title="Clients in the menu" note={cfg.on ? 'On: every owner sees Clients in Reports' : 'Off: Clients is hidden from the menu until the customer cleanup is applied'}>
        <div className="cl-pad"><AsyncButton run={() => save({ on: !cfg.on })}>{cfg.on ? 'Hide Clients' : 'Show Clients'}</AsyncButton></div>
      </Card>}
    </Screen>
  )
}
